import { DOCUMENT } from '@angular/common';
import { inject, Injectable } from '@angular/core';

/**
 * The @id of each node in the site-wide graph in `apps/landing/src/index.html`.
 *
 * A page that talks about Ledgerly refers to these rather than describing the
 * organisation or the app again. A second, slightly different description of
 * the same thing is how a crawler ends up with two entities.
 */
export const ORGANIZATION_ID = 'https://ledgerly.download/#organization';
export const WEBSITE_ID = 'https://ledgerly.download/#website';
export const APP_ID = 'https://ledgerly.download/#app';

/**
 * One `<script type="application/ld+json">` per key, in the document head.
 *
 * Angular strips `<script>` from templates, so structured data a component
 * owns has to be written into the head. Two things make that awkward, and
 * both are handled here once:
 *
 * 1. Hydration. A prerendered page already has the script in its HTML, and
 *    the component's constructor runs again in the browser. Looking the
 *    script up by key and rewriting it, instead of appending, keeps one copy.
 * 2. Navigation. The head outlives the page, so a component removes its
 *    script on destroy, or the FAQ would follow the reader into the blog.
 *
 * Writing is synchronous, so it lands in the prerendered HTML.
 */
@Injectable({ providedIn: 'root' })
export class JsonLdService {
  private readonly document = inject(DOCUMENT);

  set(key: string, data: object): void {
    let script = this.find(key);
    if (!script) {
      script = this.document.createElement('script');
      script.setAttribute('type', 'application/ld+json');
      script.setAttribute('data-ld', key);
      this.document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(data);
  }

  remove(key: string): void {
    this.find(key)?.remove();
  }

  private find(key: string): HTMLScriptElement | null {
    return this.document.head.querySelector(`script[data-ld="${key}"]`);
  }
}
